import React, { useState, useMemo } from 'react';
import { RenovationProject, Contractor, Property } from '../../types';
import { DollarSign, Wand2, Clock, Plus, Sparkles } from 'lucide-react';
import { generateRenovationEstimate } from '../../services/aiService';

interface BudgetComparisonTableProps {
  project: RenovationProject;
  onUpdate: (project: RenovationProject) => void;
  contractors: Contractor[];
  property?: Property;
}

interface BidData {
  total: number;
  tasks: Record<string, number>;
}

const BudgetComparisonTable: React.FC<BudgetComparisonTableProps> = ({ project, onUpdate, property }) => {
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [aiBaseline, setAiBaseline] = useState<number | null>(null);

  // Aggregate quotes by contractor
  const contractorBids = useMemo(() => {
    const bids: Record<string, BidData> = {};
    
    project.tasks.forEach(task => {
      if (task.quotes) {
        task.quotes.forEach(quote => {
          const providerKey = quote.providerName;
          if (!bids[providerKey]) bids[providerKey] = { total: 0, tasks: {} };
          bids[providerKey].tasks[task.id] = quote.totalAmount;
          bids[providerKey].total += quote.totalAmount;
        });
      }
    });
    return bids;
  }, [project.tasks]);

  const handleRunAI = async () => {
    setIsProcessingAI(true);
    const estimate = await generateRenovationEstimate(
      project.type, 
      property?.city || 'Portugal', 
      120, 
      project.description
    );
    
    if (estimate) {
      setAiBaseline(estimate.average);
    }
    setIsProcessingAI(false);
  };

  const handleAddBidColumn = () => {
    const name = prompt("Enter Contractor Name:");
    if (!name) return;
    
    const updatedTasks = project.tasks.map(t => ({
      ...t,
      quotes: [
        ...(t.quotes || []),
        {
          id: Date.now().toString() + Math.random(),
          providerName: name,
          totalAmount: 0,
          includes: ['Labor', 'Materials'],
          estimatedDurationDays: 0,
          isSelected: false
        }
      ]
    }));
    onUpdate({ ...project, tasks: updatedTasks });
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-emerald-500" /> Budget Matrix
        </h3>
        <div className="flex gap-3">
          <button 
            onClick={handleRunAI}
            disabled={isProcessingAI}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all disabled:opacity-70"
          >
            {isProcessingAI ? <Clock className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            AI Benchmark
          </button>
          <button 
            onClick={handleAddBidColumn}
            className="bg-slate-800 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Bidder
          </button>
        </div>
      </div>

      <div className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50 border-b border-slate-800 text-xs uppercase text-slate-500 font-bold">
                <th className="p-4 w-64 sticky left-0 bg-slate-900 z-10 border-r border-slate-800">Task</th>
                {aiBaseline !== null && (
                  <th className="p-4 w-40 text-right bg-indigo-900/10 text-indigo-400 border-r border-slate-800">
                    <div className="flex items-center justify-end gap-1">
                      <Sparkles className="w-3 h-3" /> AI Est.
                    </div>
                  </th>
                )}
                {Object.keys(contractorBids).map(bidder => (
                  <th key={bidder} className="p-4 w-40 text-right border-r border-slate-800">
                    {bidder}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-sm text-slate-300">
              {project.tasks.map(task => (
                <tr key={task.id} className="hover:bg-slate-800/30">
                  <td className="p-4 font-medium sticky left-0 bg-slate-900 z-10 border-r border-slate-800">
                    {task.title}
                  </td>
                  {aiBaseline !== null && (
                    <td className="p-4 text-right font-mono text-indigo-300 bg-indigo-900/5 border-r border-slate-800 opacity-70">
                      €{(aiBaseline / Math.max(project.tasks.length, 1)).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                  )}
                  {Object.keys(contractorBids).map(bidder => {
                    const amount = contractorBids[bidder].tasks[task.id] || 0;
                    return (
                      <td key={bidder} className="p-4 text-right border-r border-slate-800">
                        {amount > 0 ? (
                          <span className="font-mono font-bold">€{amount.toLocaleString()}</span>
                        ) : (
                          <span className="text-slate-600">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              
              <tr className="bg-slate-950 font-bold border-t-2 border-slate-700">
                <td className="p-4 sticky left-0 bg-slate-950 z-10 border-r border-slate-800 text-white">TOTALS</td>
                {aiBaseline !== null && (
                  <td className="p-4 text-right font-mono text-indigo-400 border-r border-slate-800">
                    €{aiBaseline.toLocaleString()}
                  </td>
                )}
                {Object.keys(contractorBids).map((bidder) => {
                  const data = contractorBids[bidder];
                  // Use explicit typing for Object.values to avoid 'unknown' type error
                  const allTotals = Object.values(contractorBids).map((b: BidData) => b.total);
                  if (aiBaseline) allTotals.push(aiBaseline);
                  const minTotal = Math.min(...allTotals.filter(t => t > 0));
                  const isBest = data.total === minTotal && data.total > 0;

                  return (
                    <td key={bidder} className={`p-4 text-right border-r border-slate-800 ${isBest ? 'bg-emerald-500/10' : ''}`}>
                      <div className="flex flex-col items-end">
                        <span className={`font-mono text-lg ${isBest ? 'text-emerald-400' : 'text-white'}`}>
                          €{data.total.toLocaleString()}
                        </span>
                        {isBest && (
                          <span className="text-[10px] bg-emerald-500 text-slate-900 px-1.5 rounded font-bold uppercase">Best Value</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BudgetComparisonTable;